import { AesDecrypt, AesEncrypt, keyPromise } from '../../../../Service/crypto';
import api from '../../../../api';

export interface FieldEntry {
  field_name: string;
  instruction?: string;
}

export type SectionFields = Record<string, (FieldEntry | string)[]>;

// Every field_name from the client's `selectedFields`, in section order
export async function fetchClientSelectedFieldNames(clientId?: string | null): Promise<string[]> {
  if (!clientId) return [];
  const key = await keyPromise;
  const clientResponse = await api
    .fetch('api/clientmanagement/get_client', { method: 'GET' })
    .then((r) => r.json());
  const fullClientsList = await AesDecrypt(key, clientResponse);

  const currentClient = Array.isArray(fullClientsList)
    ? fullClientsList.find((c: any) => c._id === clientId || c.id === clientId)
    : null;

  const selectedFieldsRaw = currentClient?.selectedFields;
  const selectedFieldNames: string[] = [];
  const pushField = (f: any) => {
    if (typeof f === 'object' && f?.field_name) {
      selectedFieldNames.push(f.field_name);
    } else if (typeof f === 'string') {
      selectedFieldNames.push(f);
    }
  };

  if (selectedFieldsRaw && typeof selectedFieldsRaw === 'object' && !Array.isArray(selectedFieldsRaw)) {
    Object.values(selectedFieldsRaw).forEach((sectionFields: any) => {
      if (Array.isArray(sectionFields)) sectionFields.forEach(pushField);
    });
  } else if (Array.isArray(selectedFieldsRaw)) {
    selectedFieldsRaw.forEach(pushField);
  }
  return selectedFieldNames;
}

// { 'Patient Name': '', 'Patient DOB': '', ... } — saved as `output_schema` on the template
export function buildOutputSchema(fieldNames: string[]): Record<string, string> {
  const schema: Record<string, string> = {};
  fieldNames.forEach((name) => {
    schema[name] = '';
  });
  return schema;
}

// Every field listed on the Manage Label (FieldMaster) page, in its section order.
// Not limited to a client: the designer shows the full list so any label can be dragged in.
export async function fetchMasterFields(_clientId?: string | null): Promise<SectionFields> {
  const key = await keyPromise;
  const userId = localStorage.id;

  try {
    const EncParams = await AesEncrypt(key, userId);
    const response = await api
      .fetch('api/TemplateCreation/get_patient_details', {
        method: 'POST',
        body: JSON.stringify({ EncParams }),
      })
      .then((r) => r.json());
    const decrypted = await AesDecrypt(key, response);

    if (Array.isArray(decrypted) && decrypted[0] && typeof decrypted[0] === 'object') {
      const result: SectionFields = {};
      Object.entries(decrypted[0]).forEach(([sectionKey, sectionValue]) => {
        if (sectionKey === '_id' || !sectionValue || typeof sectionValue !== 'object') return;
        const fields = (Array.isArray(sectionValue) ? sectionValue : Object.values(sectionValue)) as (FieldEntry | string)[];
        const valid = fields.filter((f) => (typeof f === 'object' ? Boolean(f?.field_name) : typeof f === 'string' && f));
        if (valid.length > 0) result[sectionKey] = valid;
      });
      return result;
    }
  } catch (error) {
    console.error('Error fetching master fields:', error);
  }

  return {};
}

export function findFieldInstruction(data: SectionFields, fieldKey: string): { sectionKey: string; instruction: string } | null {
  for (const [sectionKey, fields] of Object.entries(data)) {
    const list = Array.isArray(fields) ? fields : [];
    for (const f of list) {
      const name = typeof f === 'object' ? f.field_name : f;
      if (name === fieldKey) {
        return { sectionKey, instruction: typeof f === 'object' ? f.instruction || '' : '' };
      }
    }
  }
  return null;
}
